// ---------------------------------------------------------------------------
// Gate 4 · Slice 4.1 · Creative Studio Panel
//
// Renders inside the CampaignDetail "Creative" tab. Only usable once the
// campaign is `gate_4_ready` (parent tab already guards this).
//
// Capabilities:
//   1. "Start Creative Intake" button — POST /campaigns/{id}/creative/intake
//   2. List of existing asset_groups with their status + brief summary
//   3. Brief modal for asset_groups in `intake_verified` state
//   4. Audit timeline of creative.* events for this campaign
//
// Zero LLM. All actions deterministic on the backend.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { EmptyState, Pill } from "../../lib/ui";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ImageStudioBlock } from "./ImageStudio";
import ReelUploadPanel from "./ReelUploadPanel";
import { AssetSetsBlock } from "./AssetSetsBlock";

const BACKEND_URL = (
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  ""
);

const FORMAT_OPTIONS = [
  { value: "social_post", label: "Social post" },
  { value: "email", label: "Email" },
  { value: "display_ad", label: "Display ad" },
  { value: "landing_hero", label: "Landing hero" },
  { value: "long_form_post", label: "Long-form post" },
  { value: "video_script", label: "Video script" },
  { value: "press_release", label: "Press release" },
];

const AG_STATUS_LABEL = {
  intake_verified: { label: "Intake verified", tone: undefined },
  brief_accepted: { label: "Brief accepted", tone: undefined },
  generating: { label: "Generating", tone: "warn" },
  review_ready: { label: "Review ready", tone: "success" },
  generation_failed: { label: "Generation failed", tone: "danger" },
  blocked: { label: "Blocked by gate", tone: "danger" },
  variant_selected: { label: "Variant selected", tone: "success" },
};

const ACTION_LABEL = {
  "creative.intake_verified": { label: "Intake verified", tone: "success" },
  "creative.intake_refused": { label: "Intake refused", tone: "danger" },
  "creative.brief_accepted": { label: "Brief accepted", tone: "success" },
  "creative.text_generated": { label: "Text generated", tone: "success" },
  "creative.generate_refused": { label: "Generate refused", tone: "danger" },
  "creative.generate_failed": { label: "Generate failed", tone: "danger" },
};

function fmtTs(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(ts);
  }
}

function shortHash(h) {
  if (!h || typeof h !== "string") return "—";
  return `${h.slice(0, 8)}…${h.slice(-6)}`;
}

export default function CreativeStudioPanel({ campaignId, campaignStatus }) {
  const [assetGroups, setAssetGroups] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [approvedStatements, setApprovedStatements] = useState([]);
  const [costToday, setCostToday] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [briefTarget, setBriefTarget] = useState(null); // asset_group being briefed
  // Slice 4.2 · Text generation state
  const [textVersions, setTextVersions] = useState({}); // { asset_group_id: latest text_version | null }
  const [genBusyAg, setGenBusyAg] = useState(null); // asset_group_id currently generating
  const [genJobStatus, setGenJobStatus] = useState({}); // { ag_id: "queued"|"running" }
  // Slice 4.3 · Star Filter View — URL query param `?stars_only=1`
  const initialStarsOnly = (() => {
    try {
      return new URLSearchParams(window.location.search).get("stars_only") === "1";
    } catch { return false; }
  })();
  const [starsOnly, setStarsOnly] = useState(initialStarsOnly);
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (starsOnly) url.searchParams.set("stars_only", "1");
      else url.searchParams.delete("stars_only");
      window.history.replaceState(null, "", url.toString());
    } catch {}
  }, [starsOnly]);
  const gate4Ready = campaignStatus === "gate_4_ready";

  const reload = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setErr(null);
    try {
      const [ag, tl, stmts, cost] = await Promise.all([
        api.get(`/campaigns/${campaignId}/creative/asset-groups`),
        api.get(`/campaigns/${campaignId}/creative/timeline`),
        // Approved statements power the must_include_claims picker.
        api.get(`/intelligence/statements?limit=200`)
           .catch(() => ({ data: { statements: [] } })),
        // Slice 4.2 cost runway chip
        api.get(`/creative/cost-today`).catch(() => ({ data: null })),
      ]);
      setCostToday(cost.data || null);
      setAssetGroups(ag.data?.asset_groups || []);
      setTimeline(tl.data?.events || []);
      setApprovedStatements(
        (stmts.data?.statements || []).filter((s) => s.status === "APPROVED"),
      );
      // Fetch latest text_version for each asset_group that has ever been
      // briefed. Uses Promise.all to keep it snappy.
      const groups = ag.data?.asset_groups || [];
      const eligible = groups.filter((g) =>
        ["brief_accepted", "generating", "review_ready", "generation_failed", "blocked", "variant_selected"]
          .includes(g.status),
      );
      const results = await Promise.all(
        eligible.map((g) =>
          api.get(`/creative/asset-groups/${g.id}/text/latest`)
             .then((r) => [g.id, r.data?.text_version || null])
             .catch(() => [g.id, null]),
        ),
      );
      // For each text_version that has an edit_manual lineage, fetch its parent
      // and build a `variant.source_variant_id → parent_variant` map so the
      // Reviewer Diff UI can render field-level compare inline.
      const withParents = await Promise.all(
        results.map(async ([gid, tv]) => {
          if (!tv || !tv.parent_text_version_id) return [gid, tv];
          try {
            const p = await api.get(
              `/creative/text-versions/${tv.parent_text_version_id}`,
            );
            const pv = p.data?.text_version?.variants || [];
            const map = {};
            (tv.variants || []).forEach((v) => {
              if (v.source_variant_id) {
                const src = pv.find((x) => x.id === v.source_variant_id);
                if (src) map[v.id] = src;
              }
            });
            return [gid, { ...tv, __parent_variant_map: map }];
          } catch {
            return [gid, tv];
          }
        }),
      );
      const tv = Object.fromEntries(withParents);
      setTextVersions(tv);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    if (gate4Ready) reload();
  }, [gate4Ready, reload]);

  async function startIntake() {
    setIntakeBusy(true);
    setErr(null);
    try {
      const r = await api.post(`/campaigns/${campaignId}/creative/intake`);
      toast.success("Intake verified", {
        description: `Package sha ${shortHash(r.data?.package_sha256)} · schema ${r.data?.package_schema_version}`,
      });
      await reload();
    } catch (e) {
      const msg = formatApiError(e);
      toast.error("Intake refused", { description: msg });
      setErr(msg);
    } finally {
      setIntakeBusy(false);
    }
  }

  // Slice 4.3 — Per-version star (UI-only exploration).
  async function starVariant(textVersionId, variantId) {
    try {
      await api.post(`/creative/text-versions/${textVersionId}/star`,
                     { variant_id: variantId });
      toast.success(variantId ? "Variant starred" : "Star cleared");
      await reload();
    } catch (e) {
      toast.error("Star failed", { description: formatApiError(e) });
    }
  }

  // Slice 4.3 — Asset-group final selection (immutable pair, flips status).
  async function setFinalSelection(agId, textVersionId, variantId) {
    try {
      await api.post(`/creative/asset-groups/${agId}/final-selection`,
                     { text_version_id: textVersionId, variant_id: variantId });
      toast.success("Final variant selected", {
        description: "Asset group moved to `variant_selected` — downstream slices will use this pair.",
      });
      await reload();
    } catch (e) {
      toast.error("Selection refused", { description: formatApiError(e) });
    }
  }

  async function clearFinalSelection(agId) {
    try {
      await api.delete(`/creative/asset-groups/${agId}/final-selection`);
      toast.info("Final selection cleared");
      await reload();
    } catch (e) {
      toast.error("Clear failed", { description: formatApiError(e) });
    }
  }

  // Slice 4.4 · Guardian — deterministic re-evaluate + LLM advisory.
  async function reevaluateGuardian(textVersionId) {
    try {
      const r = await api.post(
        `/creative/text-versions/${textVersionId}/guardian/re-evaluate`,
      );
      const c = r.data?.counts || {};
      toast.success("Guardian re-evaluated", {
        description: `BLOCK ${c.BLOCK || 0} · WARN ${c.WARN || 0} · INFO ${c.INFO || 0}`,
      });
      await reload();
    } catch (e) {
      toast.error("Guardian failed", { description: formatApiError(e) });
    }
  }

  async function runAdvisory(agId, textVersionId) {
    setGenBusyAg(agId);
    setGenJobStatus((p) => ({ ...p, [agId]: "queued" }));
    try {
      const r = await api.post(
        `/creative/text-versions/${textVersionId}/guardian/advisory`,
      );
      const jobId = r.data?.job_id;
      toast.info("Advisory review starting…", {
        description: "Sonnet 4.5 · ~$0.003 · ADVISORY ONLY — never adjudicates claims",
      });
      const j = await pollJob(jobId, agId);
      if (j?.status === "succeeded") {
        toast.success("Advisory ready", {
          description: `$${(j.total_cost_usd || 0).toFixed(4)}`,
        });
      } else if (j?.status === "failed") {
        toast.error("Advisory failed", {
          description: j?.error?.message || j?.error?.code || "unknown",
        });
      }
      await reload();
    } catch (e) {
      toast.error("Advisory refused", { description: formatApiError(e) });
    } finally {
      setGenBusyAg(null);
      setGenJobStatus((prev) => {
        const { [agId]: _drop, ...rest } = prev;
        return rest;
      });
    }
  }

  async function pollJob(jobId, agId) {
    const maxTries = 36;
    for (let i = 0; i < maxTries; i += 1) {
      try {
        const r = await api.get(`/intelligence/jobs/${jobId}`);
        const j = r.data?.job || r.data;
        setGenJobStatus((prev) => ({ ...prev, [agId]: j?.status || "running" }));
        if (j?.status === "succeeded" || j?.status === "failed") {
          return j;
        }
      } catch {
        // ignore transient errors
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 2500));
    }
    return null;
  }

  async function generateText(ag) {
    setGenBusyAg(ag.id);
    setGenJobStatus((p) => ({ ...p, [ag.id]: "queued" }));
    setErr(null);
    try {
      const r = await api.post(`/creative/asset-groups/${ag.id}/generate/text`);
      const jobId = r.data?.job_id;
      toast.info("Generating text variants…", {
        description: "Claude Sonnet 4.5 · ~$0.01–0.02 per generation",
      });
      const j = await pollJob(jobId, ag.id);
      if (j?.status === "succeeded") {
        toast.success("3 variants ready", {
          description: `$${(j.total_cost_usd || 0).toFixed(4)}`,
        });
      } else if (j?.status === "failed") {
        toast.error("Generation failed", {
          description: j?.error?.message || j?.error?.code || "unknown",
        });
      } else {
        toast.warning("Still generating…", {
          description: "The job is taking longer than expected. Check back in a minute.",
        });
      }
      await reload();
    } catch (e) {
      const msg = formatApiError(e);
      toast.error("Enqueue refused", { description: msg });
      setErr(msg);
    } finally {
      setGenBusyAg(null);
      setGenJobStatus((prev) => {
        const { [ag.id]: _drop, ...rest } = prev;
        return rest;
      });
    }
  }

  // Slice 4.3 — Manual edit of one variant on an existing text_version.
  async function editVariant(agId, textVersionId, variantId, patch) {
    try {
      await api.post(
        `/creative/text-versions/${textVersionId}/edit`,
        { variant_id: variantId, ...patch },
      );
      toast.success("Edit saved", { description: "Version linked to parent" });
      await reload();
    } catch (e) {
      const msg = formatApiError(e);
      toast.error("Edit refused", { description: msg });
      throw e;
    }
  }

  // Slice 4.3 — LLM regenerate (both Vary all and Vary this variant).
  async function regenerate(agId, textVersionId, variationReason, variantId, dropVariantIds) {
    setGenBusyAg(agId);
    setGenJobStatus((p) => ({ ...p, [agId]: "queued" }));
    try {
      const r = await api.post(
        `/creative/text-versions/${textVersionId}/regenerate`,
        {
          variation_reason: variationReason,
          ...(variantId ? { variant_id: variantId } : {}),
          ...(dropVariantIds && dropVariantIds.length
              ? { drop_variant_ids: dropVariantIds } : {}),
        },
      );
      const jobId = r.data?.job_id;
      const mode = r.data?.mode;
      toast.info(
        mode === "vary_one" ? "Generating 3 alternates…" : "Regenerating all 3 variants…",
        { description: "Claude Sonnet 4.5 · ~$0.017" },
      );
      const j = await pollJob(jobId, agId);
      if (j?.status === "succeeded") {
        toast.success("Variation ready", {
          description: `$${(j.total_cost_usd || 0).toFixed(4)}`,
        });
      } else if (j?.status === "failed") {
        toast.error("Regeneration failed", {
          description: j?.error?.message || j?.error?.code || "unknown",
        });
      }
      await reload();
    } catch (e) {
      const msg = formatApiError(e);
      toast.error("Regenerate refused", { description: msg });
    } finally {
      setGenBusyAg(null);
      setGenJobStatus((prev) => {
        const { [agId]: _drop, ...rest } = prev;
        return rest;
      });
    }
  }

  if (!gate4Ready) {
    return (
      <div className="bo-card" data-testid="camp-creative-locked">
        <div className="bo-eyebrow">Gate 4 · Creative Studio</div>
        <h2 className="bo-card-title">Locked</h2>
        <p style={{ color: "var(--bo-muted)", fontSize: 13, marginTop: 8 }}>
          Creative Studio unlocks once this campaign is approved on the
          Handoff tab (status becomes <code>gate_4_ready</code>).
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 14 }} data-testid="camp-creative-panel">
      <CostRunwayChip cost={costToday} starsOnly={starsOnly} setStarsOnly={setStarsOnly} />

      <div className="bo-card" data-testid="camp-creative-intake-card">
        <div className="bo-eyebrow">Gate 4 · Slice 4.1 · Deterministic</div>
        <h2 className="bo-card-title">Creative Intake</h2>
        <p className="bo-card-lede" style={{ marginBottom: 12 }}>
          Every generation run starts from an <em>asset group</em>. Intake
          verifies the Strategy Package hash still matches, then locks that
          version as this group&rsquo;s integrity anchor.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            className="bo-btn bo-btn-primary"
            onClick={startIntake}
            disabled={intakeBusy}
            data-testid="camp-creative-intake-start-btn"
            style={{ fontSize: 12, padding: "6px 14px" }}
          >
            {intakeBusy ? "Verifying…" : "Start Creative Intake"}
          </button>
          <span
            style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
          >
            deterministic · $0.000000
          </span>
        </div>
        {err && (
          <div
            role="alert"
            data-testid="camp-creative-error"
            style={{
              marginTop: 10, fontSize: 12,
              color: "var(--bo-danger, #b34a4a)",
              background: "rgba(179, 74, 74, 0.06)",
              border: "1px solid rgba(179, 74, 74, 0.25)",
              borderRadius: 4, padding: "8px 12px",
            }}
          >
            {err}
          </div>
        )}
      </div>

      <div className="bo-card" data-testid="camp-creative-groups-card">
        <div className="bo-eyebrow">Asset Groups</div>
        <h2 className="bo-card-title">
          {assetGroups.length === 0 ? "None yet" : `${assetGroups.length} group${assetGroups.length === 1 ? "" : "s"}`}
        </h2>
        {loading && assetGroups.length === 0 ? (
          <p style={{ color: "var(--bo-muted)", fontSize: 12, marginTop: 8 }}>Loading…</p>
        ) : assetGroups.length === 0 ? (
          <EmptyState
            title="No asset groups yet"
            description='Click "Start Creative Intake" above to create the first one.'
            testId="camp-creative-groups-empty"
          />
        ) : (
          <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
            {assetGroups.map((ag, i) => (
              <AssetGroupRow
                key={ag.id}
                ag={ag}
                index={i}
                textVersion={textVersions[ag.id]}
                genBusy={genBusyAg === ag.id}
                genStatus={genJobStatus[ag.id]}
                onBrief={() => setBriefTarget(ag)}
                onGenerate={() => generateText(ag)}
                onEditVariant={(variantId, patch) => {
                  const tv = textVersions[ag.id];
                  if (!tv) return Promise.reject(new Error("no text version"));
                  return editVariant(ag.id, tv.id, variantId, patch);
                }}
                onVaryAll={(reason) => {
                  const tv = textVersions[ag.id];
                  if (!tv) return Promise.reject(new Error("no text version"));
                  return regenerate(ag.id, tv.id, reason, null, null);
                }}
                onVaryOne={(variantId, reason, dropIds) => {
                  const tv = textVersions[ag.id];
                  if (!tv) return Promise.reject(new Error("no text version"));
                  return regenerate(ag.id, tv.id, reason, variantId, dropIds);
                }}
                onStarVariant={(variantId) => {
                  const tv = textVersions[ag.id];
                  if (!tv) return;
                  return starVariant(tv.id, variantId);
                }}
                onSetFinal={(textVersionId, variantId) =>
                  setFinalSelection(ag.id, textVersionId, variantId)}
                onClearFinal={() => clearFinalSelection(ag.id)}
                onReevaluateGuardian={() => {
                  const tv = textVersions[ag.id];
                  if (tv) return reevaluateGuardian(tv.id);
                }}
                onRunAdvisory={() => {
                  const tv = textVersions[ag.id];
                  if (tv) return runAdvisory(ag.id, tv.id);
                }}
                starsOnly={starsOnly}
                onCostRefresh={() => {
                  api.get(`/creative/cost-today`)
                     .then((r) => setCostToday(r.data || null))
                     .catch(() => {});
                }}
              />
            ))}
          </div>
        )}
      </div>

      <TimelineCard events={timeline} loading={loading} />

      <BriefDialog
        open={!!briefTarget}
        assetGroup={briefTarget}
        approvedStatements={approvedStatements}
        onClose={() => setBriefTarget(null)}
        onSubmitted={async () => {
          setBriefTarget(null);
          await reload();
        }}
      />
    </div>
  );
}

function BlockedFindingsBlock({ ag, index, blockedDraft }) {
  // Hotfix 2026-10-25 — surfaces the post-generation gate findings so
  // operators can see WHY a draft was blocked (which layer, which
  // offending text) directly from Creative Studio. No LLM. No writes.
  const attempts = blockedDraft?.attempts || [];
  // Flatten findings across all attempts and all variants into a bounded,
  // human-readable list. Each row: {layer, rule, offending, message}.
  const flat = [];
  attempts.forEach((att) => {
    const perVariant = att.findings || [];
    perVariant.forEach((findingsForOneVariant) => {
      (findingsForOneVariant || []).forEach((f) => {
        flat.push({
          attempt: att.attempt_number,
          layer: f.layer || f.rule || "block",
          rule: f.rule || "",
          offending:
            (f.offenders && f.offenders.join(", "))
            || f.match_excerpt
            || f.term
            || f.pattern
            || "",
          message: f.message || f.why || "",
        });
      });
    });
  });
  const headlineReason =
    flat[0]?.layer
      ? `${flat[0].layer}${flat[0].rule ? ` · ${flat[0].rule}` : ""}`
      : (ag.last_generation_error || "blocked by gate");
  const headlineOffending = flat[0]?.offending || "";
  return (
    <div
      role="alert"
      data-testid={`camp-creative-group-${index}-blocked-panel`}
      style={{
        marginTop: 10, fontSize: 12,
        color: "var(--bo-danger, #b34a4a)",
        background: "rgba(179, 74, 74, 0.06)",
        border: "1px solid rgba(179, 74, 74, 0.25)",
        borderRadius: 4, padding: "8px 10px",
      }}
    >
      <div
        style={{ fontWeight: 600, marginBottom: 4 }}
        data-testid={`camp-creative-group-${index}-blocked-headline`}
      >
        Blocked: {headlineReason}
        {headlineOffending && (
          <span style={{ fontWeight: 400, fontStyle: "italic", marginLeft: 6 }}>
            — &ldquo;{headlineOffending.length > 120
              ? `${headlineOffending.slice(0, 117)}…`
              : headlineOffending}&rdquo;
          </span>
        )}
      </div>
      {!blockedDraft && (
        <div style={{ fontSize: 11.5, color: "var(--bo-muted)" }}>
          Owner-only detail unavailable to this role.
        </div>
      )}
      {flat.length > 0 && (
        <ul
          style={{
            margin: "4px 0 0 0", padding: "0 0 0 16px",
            fontSize: 11.5, lineHeight: 1.5,
            color: "var(--bo-text, inherit)",
          }}
          data-testid={`camp-creative-group-${index}-blocked-findings`}
        >
          {flat.slice(0, 10).map((f, i) => (
            <li key={i}>
              <span style={{ fontFamily: "var(--bo-mono)" }}>
                [attempt {f.attempt}] {f.layer}
                {f.rule ? ` · ${f.rule}` : ""}
              </span>
              {f.offending && (
                <>
                  {" — "}
                  <span style={{ fontStyle: "italic" }}>
                    &ldquo;{f.offending.length > 160
                      ? `${f.offending.slice(0, 157)}…`
                      : f.offending}&rdquo;
                  </span>
                </>
              )}
              {f.message && !f.offending && (
                <>
                  {" — "}
                  <span>{f.message}</span>
                </>
              )}
            </li>
          ))}
          {flat.length > 10 && (
            <li style={{ color: "var(--bo-muted)" }}>
              …{flat.length - 10} more finding{flat.length - 10 === 1 ? "" : "s"}
            </li>
          )}
        </ul>
      )}
      <div
        style={{
          marginTop: 6, fontSize: 11, color: "var(--bo-muted)",
          fontFamily: "var(--bo-mono)",
        }}
      >
        draft_id: {ag.last_blocked_draft_id || "—"}
      </div>
    </div>
  );
}


function AssetGroupRow({ ag, index, textVersion, genBusy, genStatus, onBrief, onGenerate, onEditVariant, onVaryAll, onVaryOne, onStarVariant, onSetFinal, onClearFinal, onReevaluateGuardian, onRunAdvisory, starsOnly, onCostRefresh }) {
  const [imageVersions, setImageVersions] = useState([]);
  const [blockedDraft, setBlockedDraft] = useState(null);
  const meta = AG_STATUS_LABEL[ag.status] || { label: ag.status, tone: "muted" };
  const brief = ag.brief;
  const canGenerate = ag.status === "brief_accepted"
    || ag.status === "generation_failed"
    || ag.status === "blocked";

  // Hotfix 2026-10-25 — when the asset_group is blocked by the post-
  // generation gate, pull the stored blocked_draft so the operator can
  // see exactly which layer fired and on what text. Owner-only on the
  // server; non-owners get a 403 which we silently fall back from.
  useEffect(() => {
    let cancel = false;
    if (ag.status !== "blocked" || !ag.last_blocked_draft_id) {
      setBlockedDraft(null);
      return () => { cancel = true; };
    }
    (async () => {
      try {
        const r = await api.get(
          `/creative/blocked-drafts/${ag.last_blocked_draft_id}`
        );
        if (!cancel) setBlockedDraft(r.data?.blocked_draft || null);
      } catch (_err) {
        if (!cancel) setBlockedDraft(null);
      }
    })();
    return () => { cancel = true; };
  }, [ag.status, ag.last_blocked_draft_id]);
  return (
    <div
      data-testid={`camp-creative-group-${index}`}
      style={{
        padding: "12px 14px",
        background: "var(--bo-panel-alt)",
        border: "1px solid var(--bo-line)",
        borderRadius: 6,
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Pill tone={meta.tone} testId={`camp-creative-group-${index}-status`}>{meta.label}</Pill>
        <span style={{ fontFamily: "var(--bo-mono)", fontSize: 11, color: "var(--bo-muted)" }}>
          sha {shortHash(ag.package_sha256)}
        </span>
        <span style={{ fontSize: 11, color: "var(--bo-muted)" }}>
          created {fmtTs(ag.created_at)}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {ag.status === "intake_verified" && (
            <button
              type="button"
              onClick={onBrief}
              className="bo-btn bo-btn-primary"
              data-testid={`camp-creative-group-${index}-brief-btn`}
              style={{ fontSize: 11, padding: "3px 10px" }}
            >
              Attach Brief
            </button>
          )}
          {canGenerate && (
            <button
              type="button"
              onClick={onGenerate}
              disabled={genBusy}
              className="bo-btn bo-btn-primary"
              data-testid={`camp-creative-group-${index}-generate-btn`}
              style={{ fontSize: 11, padding: "3px 10px" }}
              title="Generate 3 text variants via Claude Sonnet 4.5 (~$0.01)"
            >
              {genBusy
                ? (genStatus === "running" ? "Generating…" : "Queued…")
                : (textVersion ? "Regenerate variants" : "Generate 3 variants")}
            </button>
          )}
          {ag.status === "generating" && !genBusy && (
            <span
              style={{ fontSize: 11, color: "var(--bo-muted)", padding: "3px 8px" }}
              data-testid={`camp-creative-group-${index}-inflight`}
            >
              LLM in flight…
            </span>
          )}
        </div>
      </div>
      {brief && (
        <div style={{ marginTop: 10, display: "grid", gap: 4, fontSize: 12.5 }}>
          <div>
            <span className="bo-eyebrow" style={{ marginRight: 6 }}>Format</span>
            <span style={{ fontFamily: "var(--bo-mono)" }}>{brief.format}</span>
            <span style={{ margin: "0 10px", color: "var(--bo-muted)" }}>·</span>
            <span className="bo-eyebrow" style={{ marginRight: 6 }}>Channel</span>
            <span>{brief.primary_channel}</span>
          </div>
          {brief.notes && (
            <div style={{ color: "var(--bo-muted)", fontStyle: "italic" }}>
              &ldquo;{brief.notes}&rdquo;
            </div>
          )}
          {brief.tone_override && (
            <div style={{ fontSize: 11.5 }}>
              <span className="bo-eyebrow" style={{ marginRight: 6 }}>Tone override</span>
              <span>{brief.tone_override}</span>
              <span
                style={{
                  marginLeft: 8, fontSize: 10, color: "var(--bo-muted)",
                  fontFamily: "var(--bo-mono)",
                }}
                title="Guardian emits an advisory notice on every re-eval"
              >
                advisory · surfaced by Guardian
              </span>
            </div>
          )}
          {brief.must_include_claims?.length > 0 && (
            <div style={{ fontSize: 11.5 }}>
              <span className="bo-eyebrow" style={{ marginRight: 6 }}>Must include</span>
              {brief.must_include_claims.length} claim
              {brief.must_include_claims.length === 1 ? "" : "s"}
            </div>
          )}
        </div>
      )}
      {ag.last_generation_error && ag.status === "generation_failed" && (
        <div
          role="alert"
          data-testid={`camp-creative-group-${index}-gen-error`}
          style={{
            marginTop: 10, fontSize: 11.5,
            color: "var(--bo-danger, #b34a4a)",
            background: "rgba(179, 74, 74, 0.06)",
            border: "1px solid rgba(179, 74, 74, 0.25)",
            borderRadius: 4, padding: "6px 10px",
          }}
        >
          Last generation error: {ag.last_generation_error}
        </div>
      )}
      {ag.status === "blocked" && (
        <BlockedFindingsBlock
          ag={ag}
          index={index}
          blockedDraft={blockedDraft}
        />
      )}
      {textVersion && textVersion.variants?.length > 0 && (
        <TextVariantsBlock
          textVersion={textVersion}
          groupIndex={index}
          finalSelection={ag.final_selection}
          onEdit={onEditVariant}
          onVaryAll={onVaryAll}
          onVaryOne={onVaryOne}
          onStar={onStarVariant}
          onSetFinal={onSetFinal}
          onClearFinal={onClearFinal}
          onReevaluateGuardian={onReevaluateGuardian}
          onRunAdvisory={onRunAdvisory}
          starsOnly={starsOnly}
        />
      )}
      {/* Slice 4.5 · Image Studio — only surfaces once a brief is accepted */}
      {brief && (
        <ImageStudioBlock
          assetGroupId={ag.id}
          groupIndex={index}
          backendUrl={BACKEND_URL}
          onCostRefresh={onCostRefresh}
          onImageVersionsChange={setImageVersions}
        />
      )}
      {/* Slice 4.6 · Asset Sets — bundle text + image + branded preview */}
      {brief && (
        <AssetSetsBlock
          assetGroupId={ag.id}
          groupIndex={index}
          textVersion={textVersion}
          imageVersions={imageVersions}
          backendUrl={BACKEND_URL}
        />
      )}
      {/* 2026-10-23 · Reel upload (mp4 + gated caption) */}
      {brief && (
        <ReelUploadPanel assetGroupId={ag.id} />
      )}
    </div>
  );
}

function TextVariantsBlock({ textVersion, groupIndex, finalSelection, onEdit, onVaryAll, onVaryOne, onStar, onSetFinal, onClearFinal, onReevaluateGuardian, onRunAdvisory, starsOnly }) {
  const [varyAllOpen, setVaryAllOpen] = useState(false);
  const [varyReason, setVaryReason] = useState("");
  const [varyBusy, setVaryBusy] = useState(false);
  const [varyOneId, setVaryOneId] = useState(null);
  const [varyOneReason, setVaryOneReason] = useState("");
  const [dropIds, setDropIds] = useState([]);   // Q4c operator-picked drop list
  const [lineagePanel, setLineagePanel] = useState(false);

  const editKindLabel = {
    generate_new: "Fresh generation",
    edit_manual: "Manual edit",
    regenerate_all: "Vary all (LLM)",
    regenerate_one: "Vary this variant (LLM)",
  }[textVersion.edit_kind] || textVersion.edit_kind;

  async function submitVaryAll() {
    if (!varyReason.trim()) return;
    setVaryBusy(true);
    try {
      await onVaryAll(varyReason.trim());
      setVaryAllOpen(false);
      setVaryReason("");
    } finally {
      setVaryBusy(false);
    }
  }
  async function submitVaryOne(variantId) {
    if (!varyOneReason.trim()) return;
    setVaryBusy(true);
    try {
      await onVaryOne(variantId, varyOneReason.trim(), dropIds);
      setVaryOneId(null);
      setVaryOneReason("");
      setDropIds([]);
    } finally {
      setVaryBusy(false);
    }
  }

  return (
    <div
      style={{ marginTop: 14, display: "grid", gap: 10 }}
      data-testid={`camp-creative-group-${groupIndex}-variants`}
    >
      <div
        style={{
          display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
          paddingBottom: 6, borderBottom: "1px dashed var(--bo-line)",
        }}
      >
        <div className="bo-eyebrow">Generated variants</div>
        {editKindLabel && (
          <span
            data-testid={`camp-creative-group-${groupIndex}-edit-kind`}
            style={{
              fontSize: 10.5, padding: "2px 8px",
              background: "var(--bo-panel-alt)",
              border: "1px solid var(--bo-line)",
              borderRadius: 999, color: "var(--bo-muted)",
              fontFamily: "var(--bo-mono)",
            }}
          >
            {editKindLabel}
          </span>
        )}
        {textVersion.provider && (
          <span
            style={{ fontFamily: "var(--bo-mono)", fontSize: 10.5, color: "var(--bo-muted)" }}
          >
            {textVersion.provider}/{textVersion.model?.split("-").slice(0, 3).join("-")}
          </span>
        )}
        <span
          style={{ fontFamily: "var(--bo-mono)", fontSize: 10.5, color: "var(--bo-muted)" }}
        >
          ${Number(textVersion.cost_usd || 0).toFixed(4)}
          {textVersion.input_tokens ? ` · ${textVersion.input_tokens}/${textVersion.output_tokens} tok` : ""}
          {textVersion.attempts ? ` · attempts ${textVersion.attempts}` : ""}
        </span>
        <span
          style={{ fontFamily: "var(--bo-mono)", fontSize: 10.5, color: "var(--bo-muted)" }}
          title={`Generated against Strategy Package sha ${textVersion.package_sha256}`}
        >
          sha {shortHash(textVersion.package_sha256)}
        </span>
        {textVersion.parent_text_version_id && (
          <span
            data-testid={`camp-creative-group-${groupIndex}-parent-chip`}
            style={{
              fontFamily: "var(--bo-mono)", fontSize: 10.5,
              color: "var(--bo-muted)",
              padding: "2px 8px",
              background: "var(--bo-panel-alt)",
              border: "1px solid var(--bo-line)",
              borderRadius: 999,
            }}
            title={`Parent text_version: ${textVersion.parent_text_version_id}${textVersion.variation_reason ? ` · reason: ${textVersion.variation_reason}` : ""}`}
          >
            ← parent {shortHash(textVersion.parent_text_version_id)}
          </span>
        )}
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          onClick={() => setLineagePanel(true)}
          data-testid={`camp-creative-group-${groupIndex}-lineage-btn`}
          style={{ marginLeft: "auto", fontSize: 11, padding: "3px 10px" }}
          title="See the full parent chain of this text_version"
        >
          Lineage
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          onClick={onReevaluateGuardian}
          data-testid={`camp-creative-group-${groupIndex}-guardian-reeval-btn`}
          style={{ fontSize: 11, padding: "3px 10px" }}
          title="Re-run deterministic Guardian pass (rule set v1.0.0)"
        >
          Re-Guardian
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          onClick={onRunAdvisory}
          data-testid={`camp-creative-group-${groupIndex}-guardian-advisory-btn`}
          style={{ fontSize: 11, padding: "3px 10px" }}
          title="Run LLM advisory review (~$0.003 · advisory, never adjudicates)"
        >
          Advisory
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          onClick={() => setVaryAllOpen((o) => !o)}
          data-testid={`camp-creative-group-${groupIndex}-vary-all-btn`}
          style={{ fontSize: 11, padding: "3px 10px" }}
          title="Regenerate all variants with a variation hint (~$0.017 LLM)"
        >
          Vary all
        </button>
      </div>

      {finalSelection && (
        <FinalSelectionBanner
          finalSelection={finalSelection}
          textVersion={textVersion}
          groupIndex={groupIndex}
          onClear={onClearFinal}
        />
      )}

      {textVersion.variation_reason && (
        <div
          data-testid={`camp-creative-group-${groupIndex}-variation-reason`}
          style={{
            fontSize: 11.5, fontStyle: "italic",
            color: "var(--bo-muted)",
            padding: "4px 10px",
            background: "var(--bo-panel-alt)",
            borderRadius: 4,
          }}
        >
          Variation reason: &ldquo;{textVersion.variation_reason}&rdquo;
        </div>
      )}

      {varyAllOpen && (
        <VaryPrompt
          testId={`camp-creative-group-${groupIndex}-vary-all-prompt`}
          value={varyReason}
          onChange={setVaryReason}
          onSubmit={submitVaryAll}
          onCancel={() => { setVaryAllOpen(false); setVaryReason(""); }}
          busy={varyBusy}
          placeholder="e.g. try a data-driven angle; less editorial, more urgent"
        />
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 10,
        }}
      >
        {textVersion.variants
          .filter((v) => {
            if (!starsOnly) return true;
            const isStar = textVersion.star_variant_id === v.id;
            const isFinalV = finalSelection
              && finalSelection.text_version_id === textVersion.id
              && finalSelection.variant_id === v.id;
            return isStar || isFinalV;
          })
          .map((v, i) => {
          const isEdited = (textVersion.edited_variant_ids || []).includes(v.id);
          const isStarred = textVersion.star_variant_id === v.id;
          const isFinal = finalSelection
            && finalSelection.text_version_id === textVersion.id
            && finalSelection.variant_id === v.id;
          const canPrune = varyOneId && varyOneId !== v.id;
          return (
            <VariantCard
              key={v.id || i}
              variant={v}
              index={i}
              groupIndex={groupIndex}
              isEdited={isEdited && textVersion.edit_kind !== "generate_new"}
              editKind={textVersion.edit_kind}
              starred={isStarred}
              isFinal={isFinal}
              parentVariant={textVersion.__parent_variant_map?.[v.id] || null}
              canPrune={canPrune}
              droppedForPrune={dropIds.includes(v.id)}
              onTogglePrune={() => setDropIds((prev) =>
                prev.includes(v.id) ? prev.filter((x) => x !== v.id) : [...prev, v.id])}
              onEdit={(patch) => onEdit(v.id, patch)}
              onStar={() => onStar(isStarred ? null : v.id)}
              onSetFinal={() => onSetFinal(textVersion.id, v.id)}
              onVaryOne={() => { setVaryOneId(v.id); setDropIds([]); }}
              varyOneOpen={varyOneId === v.id}
              varyOneReason={varyOneReason}
              onVaryOneReasonChange={setVaryOneReason}
              onSubmitVaryOne={() => submitVaryOne(v.id)}
              onCancelVaryOne={() => { setVaryOneId(null); setVaryOneReason(""); setDropIds([]); }}
              busy={varyBusy}
            />
          );
        })}
      </div>

      <LineagePanel
        open={lineagePanel}
        onClose={() => setLineagePanel(false)}
        textVersionId={textVersion.id}
      />
    </div>
  );
}

function FinalSelectionBanner({ finalSelection, textVersion, groupIndex, onClear }) {
  const isCurrentVersion = finalSelection.text_version_id === textVersion.id;
  const finalVariant = isCurrentVersion
    ? textVersion.variants.find((v) => v.id === finalSelection.variant_id)
    : null;
  return (
    <div
      data-testid={`camp-creative-group-${groupIndex}-final-banner`}
      style={{
        padding: "10px 12px",
        background: "var(--bo-ink, #333)", color: "#fff",
        borderRadius: 4,
        display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontSize: 9.5, padding: "2px 8px",
          background: "rgba(255,255,255,0.2)",
          borderRadius: 999, fontFamily: "var(--bo-mono)",
          textTransform: "uppercase", letterSpacing: "0.05em",
        }}
      >
        Final
      </span>
      {finalVariant ? (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="bo-serif" style={{ fontSize: 15, lineHeight: 1.2 }}>
            {finalVariant.headline}
          </div>
          <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2, fontFamily: "var(--bo-mono)" }}>
            CTA: {finalVariant.cta}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, fontSize: 12, opacity: 0.85 }}>
          Final variant lives on <span style={{ fontFamily: "var(--bo-mono)" }}>
            {shortHash(finalSelection.text_version_id)}
          </span> (frozen, does not follow edits).
        </div>
      )}
      <button
        type="button"
        onClick={onClear}
        data-testid={`camp-creative-group-${groupIndex}-clear-final-btn`}
        style={{
          fontSize: 10, padding: "3px 10px",
          background: "transparent", color: "#fff",
          border: "1px solid rgba(255,255,255,0.4)",
          borderRadius: 3, cursor: "pointer",
        }}
      >
        Clear
      </button>
    </div>
  );
}

function LineagePanel({ open, onClose, textVersionId }) {
  const [chain, setChain] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !textVersionId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/creative/text-versions/${textVersionId}/lineage`)
      .then((r) => { if (!cancelled) setChain(r.data?.lineage || []); })
      .catch(() => { if (!cancelled) setChain([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, textVersionId]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="lineage-panel"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex", justifyContent: "flex-end",
        zIndex: 200,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(420px, 100%)", height: "100%",
          background: "var(--bo-panel, #fff)",
          padding: 20, overflowY: "auto",
          boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 className="bo-card-title" style={{ margin: 0 }}>Lineage</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="lineage-panel-close"
            style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer" }}
            aria-label="Close lineage panel"
          >×</button>
        </div>
        {loading ? (
          <p style={{ color: "var(--bo-muted)", fontSize: 12 }}>Loading…</p>
        ) : chain.length === 0 ? (
          <p style={{ color: "var(--bo-muted)", fontSize: 12 }}>No lineage available.</p>
        ) : (
          <ol style={{ padding: 0, margin: 0, listStyle: "none", display: "grid", gap: 10 }}>
            {chain.map((step, i) => (
              <li
                key={step.id}
                data-testid={`lineage-step-${i}`}
                style={{
                  padding: "10px 12px",
                  background: (i === chain.length - 1) ? "var(--bo-ink, #333)" : "var(--bo-panel-alt)",
                  color: (i === chain.length - 1) ? "#fff" : "var(--bo-ink)",
                  border: "1px solid var(--bo-line)",
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span
                    style={{
                      fontSize: 10, padding: "1px 6px",
                      background: "rgba(255,255,255,0.15)",
                      borderRadius: 999, fontFamily: "var(--bo-mono)",
                    }}
                  >
                    {step.edit_kind || "generate_new"}
                  </span>
                  <span style={{ fontFamily: "var(--bo-mono)", opacity: 0.8 }}>
                    {shortHash(step.id)}
                  </span>
                  <span style={{ marginLeft: "auto", fontSize: 10.5, opacity: 0.7 }}>
                    ${Number(step.cost_usd || 0).toFixed(4)} · {step.variant_count} v
                  </span>
                </div>
                {step.variation_reason && (
                  <div style={{ marginTop: 4, fontStyle: "italic", opacity: 0.85 }}>
                    &ldquo;{step.variation_reason}&rdquo;
                  </div>
                )}
                {step.pruned_variant_ids?.length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 10.5, opacity: 0.7 }}>
                    pruned {step.pruned_variant_ids.length} kept variant
                    {step.pruned_variant_ids.length === 1 ? "" : "s"}
                  </div>
                )}
                <div style={{ marginTop: 4, fontSize: 10.5, opacity: 0.65 }}>
                  {fmtTs(step.created_at)}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function VaryPrompt({ testId, value, onChange, onSubmit, onCancel, busy, placeholder }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex", flexDirection: "column", gap: 8,
        padding: 10,
        background: "var(--bo-panel-alt)",
        border: "1px solid var(--bo-line)",
        borderRadius: 6,
      }}
    >
      <div className="bo-eyebrow">Variation reason</div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder={placeholder || "Describe how the new variants should differ…"}
        disabled={busy}
        maxLength={500}
      />
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          onClick={onCancel}
          disabled={busy}
          style={{ fontSize: 11, padding: "3px 10px" }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-primary"
          onClick={onSubmit}
          disabled={busy || !value.trim()}
          style={{ fontSize: 11, padding: "3px 10px" }}
        >
          {busy ? "Generating…" : "Generate"}
        </button>
      </div>
    </div>
  );
}

function VariantCard({
  variant, index, groupIndex, isEdited, editKind,
  starred, isFinal, parentVariant, canPrune, droppedForPrune, onTogglePrune,
  onEdit, onStar, onSetFinal, onVaryOne, varyOneOpen, varyOneReason, onVaryOneReasonChange,
  onSubmitVaryOne, onCancelVaryOne, busy,
}) {
  const [editing, setEditing] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [headline, setHeadline] = useState(variant.headline);
  const [body, setBody] = useState(variant.body);
  const [cta, setCta] = useState(variant.cta);
  const [note, setNote] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  // If parent updates the variant (e.g. after Vary), refresh local buffer.
  useEffect(() => {
    if (!editing) {
      setHeadline(variant.headline);
      setBody(variant.body);
      setCta(variant.cta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant.id, variant.headline, variant.body, variant.cta]);

  const dirty = (
    headline.trim() !== variant.headline ||
    body.trim() !== variant.body ||
    cta.trim() !== variant.cta
  );

  async function save() {
    if (!dirty) { setEditing(false); return; }
    setSaveBusy(true);
    try {
      await onEdit({
        headline: headline.trim(),
        body: body.trim(),
        cta: cta.trim(),
        edit_note: note.trim() || "manual edit",
      });
      setEditing(false);
      setNote("");
    } finally {
      setSaveBusy(false);
    }
  }

  const editedBadge = isEdited && (editKind === "edit_manual" || editKind === "regenerate_one");
  const keptBadge = editKind === "regenerate_one" && !isEdited;

  return (
    <div
      data-testid={`camp-creative-group-${groupIndex}-variant-${index}`}
      style={{
        padding: "12px 14px",
        background: "var(--bo-panel, #fff)",
        border: `1px solid ${editedBadge ? "var(--bo-ink, #444)" : "var(--bo-line)"}`,
        borderRadius: 6,
        display: "flex", flexDirection: "column", gap: 8,
        position: "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div className="bo-eyebrow">Variant {index + 1}</div>
        {isFinal && (
          <span
            data-testid={`camp-creative-group-${groupIndex}-variant-${index}-final-badge`}
            style={{
              fontSize: 9.5, padding: "1px 6px",
              background: "var(--bo-ink, #333)", color: "#fff",
              borderRadius: 999, fontFamily: "var(--bo-mono)",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}
          >
            final
          </span>
        )}
        {editedBadge && (
          <span
            data-testid={`camp-creative-group-${groupIndex}-variant-${index}-edited-badge`}
            style={{
              fontSize: 9.5, padding: "1px 6px",
              background: "var(--bo-ink, #444)", color: "#fff",
              borderRadius: 999, fontFamily: "var(--bo-mono)",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}
          >
            edited
          </span>
        )}
        {keptBadge && (
          <span
            style={{
              fontSize: 9.5, padding: "1px 6px",
              background: "var(--bo-panel-alt)", color: "var(--bo-muted)",
              borderRadius: 999, fontFamily: "var(--bo-mono)",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}
          >
            kept
          </span>
        )}
        {canPrune && (
          <label
            style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 10.5, color: "var(--bo-muted)" }}
            data-testid={`camp-creative-group-${groupIndex}-variant-${index}-prune-toggle`}
          >
            <input
              type="checkbox"
              checked={!!droppedForPrune}
              onChange={onTogglePrune}
              disabled={busy}
              style={{ margin: 0 }}
            />
            drop
          </label>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          {!editing && (
            <>
              <button
                type="button"
                className="bo-btn bo-btn-ghost"
                onClick={onStar}
                data-testid={`camp-creative-group-${groupIndex}-variant-${index}-star-btn`}
                style={{ fontSize: 12, padding: "2px 6px" }}
                disabled={busy}
                title={starred ? "Unstar" : "Star this variant (UI-only exploration)"}
                aria-pressed={starred}
              >
                {starred ? "★" : "☆"}
              </button>
              <button
                type="button"
                className="bo-btn bo-btn-ghost"
                onClick={onSetFinal}
                data-testid={`camp-creative-group-${groupIndex}-variant-${index}-final-btn`}
                style={{
                  fontSize: 10, padding: "2px 8px",
                  ...(isFinal ? { background: "var(--bo-ink, #333)", color: "#fff" } : {}),
                }}
                disabled={busy || isFinal}
                title={isFinal ? "This is the final selection" : "Set as final — flips asset_group to variant_selected (frozen pair)"}
              >
                {isFinal ? "Final ✓" : "Set final"}
              </button>
              {parentVariant && (
                <button
                  type="button"
                  className="bo-btn bo-btn-ghost"
                  onClick={() => setShowDiff((s) => !s)}
                  data-testid={`camp-creative-group-${groupIndex}-variant-${index}-diff-btn`}
                  style={{ fontSize: 10, padding: "2px 8px" }}
                  disabled={busy}
                  title="Show field-level diff vs parent variant"
                >
                  {showDiff ? "Hide diff" : "Compare"}
                </button>
              )}
              <button
                type="button"
                className="bo-btn bo-btn-ghost"
                onClick={() => setEditing(true)}
                data-testid={`camp-creative-group-${groupIndex}-variant-${index}-edit-btn`}
                style={{ fontSize: 10, padding: "2px 8px", opacity: isFinal ? 0.35 : 1 }}
                disabled={busy || isFinal}
                title={isFinal ? "Finalized variant is frozen — clear final to edit" : "Manual edit (deterministic, $0)"}
              >
                Edit
              </button>
              <button
                type="button"
                className="bo-btn bo-btn-ghost"
                onClick={onVaryOne}
                data-testid={`camp-creative-group-${groupIndex}-variant-${index}-vary-btn`}
                style={{ fontSize: 10, padding: "2px 8px", opacity: isFinal ? 0.35 : 1 }}
                disabled={busy || isFinal}
                title={isFinal ? "Finalized variant is frozen — clear final to vary" : "Regenerate 3 alternates of this variant (~$0.017 LLM)"}
              >
                Vary this
              </button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div style={{ display: "grid", gap: 6 }}>
          <Input
            data-testid={`camp-creative-group-${groupIndex}-variant-${index}-headline-input`}
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            maxLength={200}
            disabled={saveBusy}
            style={{ fontFamily: "var(--bo-serif)", fontSize: 15 }}
          />
          <Textarea
            data-testid={`camp-creative-group-${groupIndex}-variant-${index}-body-input`}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            maxLength={2000}
            disabled={saveBusy}
          />
          <Input
            data-testid={`camp-creative-group-${groupIndex}-variant-${index}-cta-input`}
            value={cta}
            onChange={(e) => setCta(e.target.value)}
            maxLength={120}
            placeholder="CTA"
            disabled={saveBusy}
          />
          <Input
            data-testid={`camp-creative-group-${groupIndex}-variant-${index}-note-input`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="Edit note (optional)"
            disabled={saveBusy}
            style={{ fontSize: 11 }}
          />
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="bo-btn bo-btn-ghost"
              onClick={() => setEditing(false)}
              disabled={saveBusy}
              style={{ fontSize: 11, padding: "3px 10px" }}
              data-testid={`camp-creative-group-${groupIndex}-variant-${index}-cancel-btn`}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bo-btn bo-btn-primary"
              onClick={save}
              disabled={saveBusy || !dirty}
              style={{ fontSize: 11, padding: "3px 10px" }}
              data-testid={`camp-creative-group-${groupIndex}-variant-${index}-save-btn`}
            >
              {saveBusy ? "Saving…" : "Save edit"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            className="bo-serif"
            style={{ fontSize: 16, lineHeight: 1.25, letterSpacing: "-0.005em" }}
            data-testid={`camp-creative-group-${groupIndex}-variant-${index}-headline`}
          >
            {variant.headline}
          </div>
          <div
            style={{
              fontSize: 12.5, color: "var(--bo-ink-soft, var(--bo-muted))",
              whiteSpace: "pre-wrap", lineHeight: 1.5,
            }}
            data-testid={`camp-creative-group-${groupIndex}-variant-${index}-body`}
          >
            {variant.body}
          </div>
          <div>
            <span className="bo-eyebrow" style={{ marginRight: 6 }}>CTA</span>
            <span
              style={{ fontWeight: 600, fontSize: 12.5 }}
              data-testid={`camp-creative-group-${groupIndex}-variant-${index}-cta`}
            >
              {variant.cta}
            </span>
          </div>
          {variant.rationale && (
            <div
              style={{
                fontSize: 11, color: "var(--bo-muted)",
                fontStyle: "italic",
                paddingTop: 6,
                borderTop: "1px dashed var(--bo-line)",
              }}
            >
              {variant.rationale}
            </div>
          )}
        </>
      )}

      {showDiff && parentVariant && !editing && (
        <div
          data-testid={`camp-creative-group-${groupIndex}-variant-${index}-diff`}
          style={{
            marginTop: 6, padding: 8,
            background: "var(--bo-panel-alt)",
            border: "1px dashed var(--bo-line)",
            borderRadius: 4, fontSize: 11.5,
            display: "grid", gap: 6,
          }}
        >
          <div className="bo-eyebrow">Diff vs parent variant</div>
          {["headline", "body", "cta"].map((field) => {
            const before = parentVariant[field] || "";
            const after = variant[field] || "";
            if (before === after) {
              return (
                <div key={field} style={{ display: "grid", gridTemplateColumns: "50px 1fr", gap: 6, color: "var(--bo-muted)" }}>
                  <span className="bo-eyebrow">{field}</span>
                  <span style={{ fontFamily: field === "cta" ? "inherit" : undefined }}>· unchanged ·</span>
                </div>
              );
            }
            return (
              <div key={field} style={{ display: "grid", gridTemplateColumns: "50px 1fr", gap: 6 }}>
                <span className="bo-eyebrow">{field}</span>
                <div style={{ display: "grid", gap: 2 }}>
                  <div style={{ background: "rgba(179,74,74,0.12)", padding: "2px 6px", borderRadius: 2 }}>
                    <span style={{ color: "var(--bo-danger, #b34a4a)", fontWeight: 600, marginRight: 4 }}>−</span>
                    {before}
                  </div>
                  <div style={{ background: "rgba(76,132,64,0.14)", padding: "2px 6px", borderRadius: 2 }}>
                    <span style={{ color: "#4c8440", fontWeight: 600, marginRight: 4 }}>+</span>
                    {after}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {varyOneOpen && !editing && (
        <VaryPrompt
          testId={`camp-creative-group-${groupIndex}-variant-${index}-vary-prompt`}
          value={varyOneReason}
          onChange={onVaryOneReasonChange}
          onSubmit={onSubmitVaryOne}
          onCancel={onCancelVaryOne}
          busy={busy}
          placeholder="e.g. keep the core promise, but make it warmer"
        />
      )}

      <GuardianAnnotation variant={variant} groupIndex={groupIndex} index={index} />
    </div>
  );
}

function GuardianAnnotation({ variant, groupIndex, index }) {
  const [expanded, setExpanded] = useState(false);
  const latest = variant.guardian_latest;
  const advisory = variant.guardian_advisory_latest;
  if (!latest && !advisory) return null;
  const counts = latest?.counts || {};
  const blockN = counts.BLOCK || 0;
  const warnN = counts.WARN || 0;
  const infoN = counts.INFO || 0;
  const total = blockN + warnN + infoN;
  const tone = blockN > 0 ? "danger" : warnN > 0 ? "warn" : undefined;
  return (
    <div
      style={{
        marginTop: 6, paddingTop: 6,
        borderTop: "1px dashed var(--bo-line)",
        display: "grid", gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          data-testid={`camp-creative-group-${groupIndex}-variant-${index}-guardian-pill`}
          style={{
            fontSize: 10.5, padding: "2px 8px",
            background: "transparent",
            border: `1px solid ${blockN > 0 ? "var(--bo-danger, #b34a4a)" : warnN > 0 ? "#b8860b" : "var(--bo-line)"}`,
            borderRadius: 999,
            color: blockN > 0 ? "var(--bo-danger, #b34a4a)" : warnN > 0 ? "#b8860b" : "var(--bo-muted)",
            cursor: "pointer",
            fontFamily: "var(--bo-mono)",
          }}
          aria-expanded={expanded}
          title={`Guardian rule set ${latest?.rule_set_version || "?"}`}
        >
          Guardian: {blockN > 0 ? `${blockN} BLOCK` : ""}
          {blockN > 0 && (warnN + infoN > 0) ? " · " : ""}
          {warnN > 0 ? `${warnN} WARN` : ""}
          {warnN > 0 && infoN > 0 ? " · " : ""}
          {infoN > 0 ? `${infoN} INFO` : ""}
          {total === 0 ? "clean" : ""}
        </button>
        {advisory && (
          <span
            data-testid={`camp-creative-group-${groupIndex}-variant-${index}-advisory-pill`}
            style={{
              fontSize: 10.5, padding: "2px 8px",
              background: "var(--bo-panel-alt)",
              border: "1px dashed var(--bo-line)", borderRadius: 999,
              color: "var(--bo-muted)", fontFamily: "var(--bo-mono)",
            }}
            title={`Advisory (${advisory.provider}/${advisory.model?.split("-").slice(0,3).join("-")}) · $${(advisory.cost_usd_share || 0).toFixed(4)}`}
          >
            ADVISORY · tone {advisory.tone_fit_score}/100
          </span>
        )}
      </div>
      {expanded && (
        <div
          data-testid={`camp-creative-group-${groupIndex}-variant-${index}-guardian-panel`}
          style={{
            padding: 8, background: "var(--bo-panel-alt)",
            border: "1px solid var(--bo-line)", borderRadius: 4,
            display: "grid", gap: 6, fontSize: 11.5,
          }}
        >
          {latest?.findings?.length > 0 ? (
            <ul style={{ padding: 0, margin: 0, listStyle: "none", display: "grid", gap: 4 }}>
              {latest.findings.map((f, i) => (
                <li key={i} style={{ display: "grid", gridTemplateColumns: "58px 1fr", gap: 6 }}>
                  <span
                    style={{
                      fontSize: 9.5, padding: "1px 6px",
                      background: f.severity === "BLOCK" ? "var(--bo-danger, #b34a4a)" :
                                  f.severity === "WARN" ? "#b8860b" : "var(--bo-line)",
                      color: f.severity === "INFO" ? "var(--bo-muted)" : "#fff",
                      borderRadius: 3, fontFamily: "var(--bo-mono)",
                      textAlign: "center", height: 16,
                    }}
                  >
                    {f.severity}
                  </span>
                  <span>{f.message}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ color: "var(--bo-muted)" }}>No deterministic findings.</div>
          )}
          {advisory && (
            <div
              style={{
                marginTop: 4, padding: 6,
                background: "rgba(0,0,0,0.04)", borderRadius: 3,
                display: "grid", gap: 4,
              }}
            >
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span
                  style={{
                    fontSize: 9.5, padding: "1px 6px",
                    background: "var(--bo-muted)", color: "#fff",
                    borderRadius: 3, fontFamily: "var(--bo-mono)",
                    textTransform: "uppercase", letterSpacing: "0.04em",
                  }}
                >
                  Advisory
                </span>
                <span style={{ fontSize: 10.5, color: "var(--bo-muted)" }}>
                  ({advisory.provider}) · tone {advisory.tone_fit_score}/100
                </span>
              </div>
              {advisory.tone_notes && (
                <div><span className="bo-eyebrow" style={{ marginRight: 4 }}>Tone</span>{advisory.tone_notes}</div>
              )}
              {advisory.register_notes && (
                <div><span className="bo-eyebrow" style={{ marginRight: 4 }}>Register</span>{advisory.register_notes}</div>
              )}
              {advisory.potential_unsupported_claims?.length > 0 && (
                <div>
                  <span className="bo-eyebrow" style={{ marginRight: 4 }}>Potential unsupported</span>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {advisory.potential_unsupported_claims.map((c, i) => (
                      <li key={i} style={{ fontStyle: "italic" }}>{c}</li>
                    ))}
                  </ul>
                  <div style={{ fontSize: 10, color: "var(--bo-muted)", marginTop: 2 }}>
                    Advisory suspicions only — never adjudicates claim validity.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function CostRunwayChip({ cost }) {
  if (!cost) return null;
  const ceiling = Number(cost.ceiling_usd || 3);
  const spent = Number(cost.total_cost_usd || 0);
  const remaining = Number(cost.remaining_usd || 0);
  const pct = ceiling > 0 ? Math.min(100, (spent / ceiling) * 100) : 0;
  const overCeiling = spent >= ceiling;
  const nearCeiling = pct >= 70 && !overCeiling;

  const tone = overCeiling ? "danger" : nearCeiling ? "warn" : undefined;
  const bar = overCeiling
    ? "var(--bo-danger, #b34a4a)"
    : nearCeiling
    ? "#b8860b"
    : "var(--bo-ink, #444)";

  return (
    <div
      className="bo-card"
      data-testid="camp-creative-cost-chip"
      style={{ padding: "12px 14px" }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div className="bo-eyebrow" style={{ marginRight: 4 }}>
          Gate 4 spend today
        </div>
        <div
          style={{
            fontFamily: "var(--bo-mono)", fontSize: 15, fontWeight: 600,
            color: overCeiling ? "var(--bo-danger, #b34a4a)" : "var(--bo-ink)",
          }}
          data-testid="camp-creative-cost-chip-total"
        >
          ${spent.toFixed(4)}
        </div>
        <div style={{ fontFamily: "var(--bo-mono)", fontSize: 11, color: "var(--bo-muted)" }}>
          / ${ceiling.toFixed(2)} ceiling · ${remaining.toFixed(4)} left
        </div>
        <Pill tone={tone} testId="camp-creative-cost-chip-calls">
          {cost.call_count} {cost.call_count === 1 ? "call" : "calls"}
        </Pill>
        {Object.entries(cost.by_slice || {}).map(([sid, b]) => (
          <span
            key={sid}
            data-testid={`camp-creative-cost-chip-slice-${sid}`}
            style={{
              fontFamily: "var(--bo-mono)", fontSize: 10.5,
              color: "var(--bo-muted)",
              padding: "2px 8px",
              background: "var(--bo-panel-alt)",
              border: "1px solid var(--bo-line)",
              borderRadius: 999,
            }}
          >
            slice {sid} · ${Number(b.cost_usd || 0).toFixed(4)} · {b.calls}×
          </span>
        ))}
      </div>
      <div
        style={{
          marginTop: 10, height: 4,
          background: "var(--bo-panel-alt)",
          borderRadius: 999, overflow: "hidden",
        }}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Gate 4 daily budget usage"
      >
        <div
          style={{
            width: `${pct}%`, height: "100%", background: bar,
            transition: "width 220ms ease",
          }}
        />
      </div>
    </div>
  );
}



function TimelineCard({ events, loading }) {
  return (
    <div className="bo-card" data-testid="camp-creative-timeline-card">
      <div className="bo-eyebrow">Governance timeline</div>
      <h2 className="bo-card-title">Audit events</h2>      {loading && events.length === 0 ? (
        <p style={{ color: "var(--bo-muted)", fontSize: 12, marginTop: 8 }}>Loading…</p>
      ) : events.length === 0 ? (
        <p style={{ color: "var(--bo-muted)", fontSize: 12, marginTop: 8 }} data-testid="camp-creative-timeline-empty">
          No creative events yet. Actions here (intake, brief, refusals) will
          be recorded for every asset group in this campaign.
        </p>
      ) : (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }} data-testid="camp-creative-timeline">
          {events.map((ev, i) => {
            const meta = ACTION_LABEL[ev.action] || { label: ev.action, tone: "muted" };
            return (
              <div
                key={ev.id || i}
                data-testid={`camp-creative-timeline-row-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  gap: 10,
                  alignItems: "center",
                  padding: "8px 10px",
                  background: "var(--bo-panel-alt)",
                  border: "1px solid var(--bo-line)",
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                <Pill tone={meta.tone}>{meta.label}</Pill>
                <div style={{ minWidth: 0 }}>
                  {ev.metadata?.reason && (
                    <div style={{ color: "var(--bo-danger, #b34a4a)", fontSize: 11.5 }}>
                      {ev.metadata.reason}
                    </div>
                  )}
                  {ev.metadata?.format && (
                    <div style={{ fontFamily: "var(--bo-mono)", fontSize: 11 }}>
                      {ev.metadata.format}
                      {ev.metadata?.must_include_claims_count > 0 && (
                        <span style={{ marginLeft: 6, color: "var(--bo-muted)" }}>
                          · {ev.metadata.must_include_claims_count} claim
                          {ev.metadata.must_include_claims_count === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  )}
                  {ev.metadata?.package_sha256 && (
                    <div
                      style={{
                        fontFamily: "var(--bo-mono)", fontSize: 10.5,
                        color: "var(--bo-muted)",
                      }}
                    >
                      sha {shortHash(ev.metadata.package_sha256)}
                    </div>
                  )}
                </div>
                <div style={{ color: "var(--bo-muted)", fontSize: 11, whiteSpace: "nowrap" }}>
                  {fmtTs(ev.occurred_at)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BriefDialog({ open, assetGroup, approvedStatements, onClose, onSubmitted }) {
  const [format, setFormat] = useState("social_post");
  const [primaryChannel, setPrimaryChannel] = useState("");
  const [notes, setNotes] = useState("");
  const [toneOverride, setToneOverride] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Reset when opening for a new asset_group.
  useEffect(() => {
    if (open) {
      setFormat("social_post");
      setPrimaryChannel("");
      setNotes("");
      setToneOverride("");
      setWidth("");
      setHeight("");
      setSelectedClaims([]);
      setErr(null);
    }
  }, [open, assetGroup?.id]);

  async function submit() {
    if (!assetGroup) return;
    if (!primaryChannel.trim()) {
      setErr("Primary channel is required.");
      return;
    }
    setBusy(true);
    setErr(null);
    const body = {
      format,
      primary_channel: primaryChannel.trim(),
      notes: notes.trim() || null,
      tone_override: toneOverride.trim() || null,
      must_include_claims: selectedClaims,
    };
    if (width && height) {
      body.dimensions = { width: parseInt(width, 10), height: parseInt(height, 10) };
    }
    try {
      await api.post(`/creative/asset-groups/${assetGroup.id}/brief`, body);
      toast.success("Brief accepted", {
        description: `${format} · ${primaryChannel}`,
      });
      onSubmitted();
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toast.error("Brief rejected", { description: msg });
    } finally {
      setBusy(false);
    }
  }

  function toggleClaim(id) {
    setSelectedClaims((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="camp-creative-brief-dialog" style={{ maxWidth: 560 }}>
        <DialogHeader>
          <DialogTitle>Attach Creative Brief</DialogTitle>
        </DialogHeader>
        <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
          <div>
            <Label htmlFor="brief-format">Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger id="brief-format" data-testid="brief-format-select" style={{ marginTop: 4 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMAT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`brief-format-opt-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="brief-channel">Primary channel</Label>
            <Input
              id="brief-channel"
              data-testid="brief-channel-input"
              value={primaryChannel}
              onChange={(e) => setPrimaryChannel(e.target.value)}
              placeholder="LinkedIn, Newsletter, Homepage…"
              disabled={busy}
              style={{ marginTop: 4 }}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <Label htmlFor="brief-w">Width (optional)</Label>
              <Input
                id="brief-w" type="number" min={1} max={8000}
                data-testid="brief-width-input"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                disabled={busy}
                placeholder="1200"
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              <Label htmlFor="brief-h">Height (optional)</Label>
              <Input
                id="brief-h" type="number" min={1} max={8000}
                data-testid="brief-height-input"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                disabled={busy}
                placeholder="628"
                style={{ marginTop: 4 }}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="brief-notes">Notes (optional)</Label>
            <Textarea
              id="brief-notes"
              data-testid="brief-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              disabled={busy}
              placeholder="Context, objective, angle…"
              style={{ marginTop: 4 }}
            />
          </div>
          <div>
            <Label htmlFor="brief-tone">
              Tone override (optional)
              <span style={{ marginLeft: 8, fontSize: 11, color: "var(--bo-muted)" }}>
                Guardian will surface an advisory notice when set
              </span>
            </Label>
            <Input
              id="brief-tone"
              data-testid="brief-tone-input"
              value={toneOverride}
              onChange={(e) => setToneOverride(e.target.value)}
              disabled={busy}
              placeholder="extra confident, playful…"
              style={{ marginTop: 4 }}
              maxLength={200}
            />
          </div>
          {approvedStatements.length > 0 && (
            <div>
              <Label>Must-include claims (optional)</Label>
              <div
                style={{
                  marginTop: 4,
                  maxHeight: 140, overflowY: "auto",
                  border: "1px solid var(--bo-line)",
                  borderRadius: 4, padding: 6,
                  display: "grid", gap: 4,
                }}
                data-testid="brief-claims-list"
              >
                {approvedStatements.slice(0, 40).map((s) => (
                  <label
                    key={s.id}
                    style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 11.5, cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedClaims.includes(s.id)}
                      onChange={() => toggleClaim(s.id)}
                      data-testid={`brief-claim-${s.id}`}
                      disabled={busy}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      <span style={{ fontFamily: "var(--bo-mono)", color: "var(--bo-muted)", marginRight: 6 }}>
                        {s.field_path}
                      </span>
                      <span>
                        {String(s.value || "").slice(0, 90)}
                        {String(s.value || "").length > 90 ? "…" : ""}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {err && (
            <div
              role="alert"
              data-testid="camp-creative-brief-error"
              style={{
                fontSize: 12,
                color: "var(--bo-danger, #b34a4a)",
                background: "rgba(179, 74, 74, 0.06)",
                border: "1px solid rgba(179, 74, 74, 0.25)",
                borderRadius: 4, padding: "8px 12px",
              }}
            >
              {err}
            </div>
          )}
        </div>
        <DialogFooter>
          <button
            type="button"
            className="bo-btn bo-btn-ghost"
            onClick={onClose}
            disabled={busy}
            data-testid="camp-creative-brief-cancel-btn"
            style={{ fontSize: 12, padding: "6px 14px" }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bo-btn bo-btn-primary"
            onClick={submit}
            disabled={busy || !primaryChannel.trim()}
            data-testid="camp-creative-brief-submit-btn"
            style={{ fontSize: 12, padding: "6px 14px" }}
          >
            {busy ? "Submitting…" : "Submit Brief"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
